import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Grid } from '@mui/material';
import { runScenario,
  fetchCashflow,
  fetchMetrics,
  fetchMetricsMode,
  fetchScenarioTelemetry } from '../api';
import {
  useNotification,
  isPermissionDenied,
  getPermissionDeniedMessage,
  notifyPermissionError,
} from '@1823-partners/core';
import PortfolioForm from './PortfolioForm';
import CusipTables from './CusipTables';
import { notifyUngatedFailure } from '../utils/notifyDenial';
import { SITE_ENV } from '../utils/siteEnv';
import { RUN_OUTCOME_UNKNOWN, serverErrorText } from '../utils/serverErrors';

// The scenario server's terminal telemetry statuses (apps/scenario/server.py).
const TERMINAL = new Set(['COMPLETED', 'COMPLETED WITH ERRORS', 'FAILED', 'SKIPPED']);
const RUN_FAILED =
  'The scenario run failed, so it produced no new results. Check the run status on the Dashboard for details.';
const RUN_ALL_SKIPPED =
  'Every task of the scenario run was skipped, so it produced no new results. ' +
  'Check the run status on the Dashboard for details.';
// /cashflow and /metrics return the latest rows per scenario across runs, so a
// task that errored, failed or was skipped leaves an earlier run's rows in its place.
const RUN_PARTIAL =
  'The scenario run finished with errors, failed or skipped tasks, so some results may be missing or ' +
  'from an earlier run. Check the run status on the Dashboard for details.';
const RUN_UNCONFIRMED =
  'The status of some tasks of the scenario run could not be confirmed, so some results may be missing ' +
  'or from an earlier run. Check the run status on the Dashboard for details.';
const RUN_STATUS_UNAVAILABLE =
  'Could not load the scenario run status or results. Check the run status on the Dashboard.';
const RUN_NONE_CONFIRMED =
  'The status of some tasks of the scenario run could not be confirmed, and none was confirmed to ' +
  'produce results, so no results were loaded. Check the run status on the Dashboard for details.';
const RUN_STATUS_MISSING =
  'Could not find any status for the scenario run, so no results were loaded. ' +
  'Check the run status on the Dashboard.';
// Polls (5 s apart) with nothing of the run active before tasks that never
// reported are given up on: ten minutes, since a task can sit in the Batch queue
// with its best-effort SUBMITTED row lost. And consecutive telemetry failures
// before the wait reports an error.
const IDLE_POLLS = 120;
const FAILED_POLLS = 3;
const RUN_UNTRACKABLE =
  'The run was submitted, but the server did not say which tasks to wait for. Check the run status on the Dashboard.';

const PortfolioAnalysis = ({ userId, prefilledData, onDataUsed }) => {
  const { showNotification } = useNotification();

  // const [formData, setFormData] = useState({
  //   cusips: ['110331PN9','60637BFA3'],
  //   asofdt: '2025-03-31',
  //   env: 'UAT',
  //   scenarios: ['US0001'],
  // });

  // Effect to handle prefilled data
  useEffect(() => {
    if (prefilledData) {
      setFormData(prefilledData);
      // Call onDataUsed to clear the prefilled data after using it
      if (onDataUsed) {
        onDataUsed();
      }
    }
  }, [prefilledData, onDataUsed]);

  const [formData, setFormData] = useState({
    id: null,
    cusips: [],
    asofdt: '',
    env: SITE_ENV,
    scenarios: [],
    description: '',
    adcoCshfBond: false,
    adcoCshfLoan: false,
    modifiedDate: null,
  });


  const [cashflowData, setCashflowData] = useState([]);
  const [metricsData, setMetricsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  // The run being waited on: its Batch job and tasks (from /run_scenario) and the
  // inputs it was submitted with. A new object per submission, so re-running the
  // same config starts a new wait.
  const [activeRun, setActiveRun] = useState(null);
  const [error, setError] = useState(null);
  // Which flow's failure the banner is currently showing. Inputs stay editable
  // during a run, so editing them starts a fresh fetchPastResults *after*
  // submission — one that invalidateActiveFetch below cannot have invalidated.
  // If the run then fails, that fetch succeeding would clear the run's message
  // out from under the user. A fetch may clear its own message; it may not clear
  // the run's.
  const errorSourceRef = useRef(null);

  // One setter for the banner, so every path that reports a failure goes through
  // the same place. No denial object rides along: /cashflow and /metrics carry no
  // @require_permission, so a permission_denied from them would mean an auth or
  // proxy problem rather than a missing role, and there would be no role to
  // request. Only save_configs, run_scenario and upload_scenarios are gated, and
  // those report through notifyPermissionError, which attaches its own action.
  const showError = (message, source = null) => {
    setError(message);
    errorSourceRef.current = message ? source : null;
  };
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [isWaitingForConfig, setIsWaitingForConfig] = useState(false);
  // Off by default: Metrics Mode posts to /portfolio_analytics, which the scenario
  // server does not serve. Bond analytics need the engine, which runs only on Azure
  // Batch (web images never ship it), so a run goes through /run_scenario instead.
  const [metricsMode, setMetricsMode] = useState(false);
  const [selectedLotIds, setSelectedLotIds] = useState([]); // LOT_ID filter
  
  // Only the newest fetchPastResults may write shared state. The effect below
  // re-fires on every formData change, so requests overlap: without this, an
  // older denial can land after a newer success and leave a Request access
  // button naming a role for a portfolio the user is no longer looking at.
  const requestIdRef = useRef(0);

  // Abandon whatever fetch is in flight. The spinner has to be cleared here
  // rather than left to that request's finally: it now sees a newer id and
  // skips its own cleanup, so with no replacement fetch the grids would spin
  // indefinitely.
  const invalidateActiveFetch = () => {
    requestIdRef.current += 1;
    setTableLoading(false);
  };

  const pollingIntervalRef = useRef(null);

  // Extract unique LOT_IDs from both datasets
  const availableLotIds = useMemo(() => {
    const lotIdSet = new Set();
    
    cashflowData.forEach(row => {
      if (row.LOT_ID != null) {
        lotIdSet.add(String(row.LOT_ID));
      }
    });
    
    metricsData.forEach(row => {
      if (row.LOT_ID != null) {
        lotIdSet.add(String(row.LOT_ID));
      }
    });
    
    return Array.from(lotIdSet).sort();
  }, [cashflowData, metricsData]);

  // Filter data based on selected LOT_IDs
  const filteredCashflowData = useMemo(() => {
    if (selectedLotIds.length === 0) return cashflowData;
    return cashflowData.filter(row => selectedLotIds.includes(String(row.LOT_ID)));
  }, [cashflowData, selectedLotIds]);

  const filteredMetricsData = useMemo(() => {
    if (selectedLotIds.length === 0) return metricsData;
    return metricsData.filter(row => selectedLotIds.includes(String(row.LOT_ID)));
  }, [metricsData, selectedLotIds]);

  // Enhanced data cleaning function
  const cleanDataForGrid = (data) => {
    if (!Array.isArray(data)) {
      console.warn('Data is not an array:', typeof data, data);
      return [];
    }

    console.log('Cleaning data, original length:', data.length);
    
    const cleaned = data.map((row, index) => {
      const cleanRow = {};
      
      Object.keys(row).forEach(key => {
        let value = row[key];
        
        // Handle different types of "invalid" values
        if (value === null || value === undefined) {
          cleanRow[key] = null;
        } else if (typeof value === 'number' && (isNaN(value) || !isFinite(value))) {
          cleanRow[key] = null;
        } else if (typeof value === 'string') {
          // Handle string representations of NaN or null
          const trimmed = value.trim();
          if (trimmed === 'NaN' || trimmed === 'null' || trimmed === 'undefined' || trimmed === '') {
            cleanRow[key] = null;
          } else {
            cleanRow[key] = value;
          }
        } else {
          cleanRow[key] = value;
        }
      });
      
      // Debug first few rows
      if (index < 3) {
        console.log(`Row ${index} cleaned:`, {
          CUSIP: cleanRow.CUSIP,
          CLEAN_PX: cleanRow.CLEAN_PX,
          BOOK_VALUE: cleanRow.BOOK_VALUE,
          GL: cleanRow.GL
        });
      }
      
      return cleanRow;
    });

    console.log('Data cleaned, final length:', cleaned.length);
    return cleaned;
  };

  // Watch for form completion
  useEffect(() => {
    console.log('Form data changed:', formData);
    const isComplete = formData.cusips && 
                      formData.cusips.length > 0 &&
                      formData.asofdt && 
                      formData.scenarios.length > 0;
    
    console.log('Is form complete?', isComplete);
    setIsFormComplete(isComplete);
    
    if (isComplete) {
      console.log('Form complete, fetching past results...');
      fetchPastResults(isComplete);
    } else {
      console.log('Form incomplete, clearing data');
      // Clear data if form becomes incomplete. The error goes too: it described
      // inputs the user has since changed, and a stale denial would keep
      // offering a Request access button for them.
      setCashflowData([]);
      setMetricsData([]);
      showError(null);
      invalidateActiveFetch();
    }
    // Re-run only when an input that decides completeness changes. fetchPastResults is a new
    // closure every render and reads this render's formData, so listing it (or all of formData)
    // would refetch on every render or on edits that cannot change the results.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.cusips, formData.asofdt, formData.scenarios, formData.env]);

  const fetchPastResults = async (isComplete) => {
    console.log('fetchPastResults called, isComplete:', isComplete);
    if (!isComplete) {
      console.log('Form not complete, returning early');
      return;
    }
    
    const payload = {
      cusips: formData.cusips,
      scenarios: formData.scenarios,
      asofdt: formData.asofdt,
      env: formData.env
    };
    
    console.log('Fetching past results with payload:', payload);

    const requestId = ++requestIdRef.current;
    setTableLoading(true);
    try {
      console.log('Making API calls with payload:', payload);
      const [cashflowResponse, metricsResponse] = await Promise.all([
        fetchCashflow(payload),
        fetchMetrics(payload)
      ]);

      console.log('API Response received:');
      console.log('- Cashflow data length:', cashflowResponse?.data?.length || 0);
      console.log('- Metrics data length:', metricsResponse?.data?.length || 0);
      console.log('- Metrics response type:', typeof metricsResponse?.data);
      console.log('- Metrics is array:', Array.isArray(metricsResponse?.data));

      // A newer fetch started while this one was in flight: its results are the
      // ones the user is waiting for, so drop these.
      if (requestId !== requestIdRef.current) return;

      // This attempt succeeded, so an earlier *fetch* failure no longer applies.
      // A run failure is left alone: it is about a different operation, and the
      // user has not seen it resolve.
      if (errorSourceRef.current !== 'run') {
        showError(null);
      }

      // Clean the data using enhanced cleaning function
      const cleanCashflowData = cleanDataForGrid(cashflowResponse?.data || []);
      const cleanMetricsData = cleanDataForGrid(metricsResponse?.data || []);

      console.log('After cleaning:');
      console.log('- Clean cashflow length:', cleanCashflowData.length);
      console.log('- Clean metrics length:', cleanMetricsData.length);

      // Force state updates with functional setState to ensure they trigger
      setCashflowData(prevData => {
        console.log('Setting cashflow data, prev length:', prevData?.length || 0, 'new length:', cleanCashflowData.length);
        return cleanCashflowData;
      });
      
      setMetricsData(prevData => {
        console.log('Setting metrics data, prev length:', prevData?.length || 0, 'new length:', cleanMetricsData.length);
        return cleanMetricsData;
      });

      // Add a small delay to ensure state has updated before logging
      setTimeout(() => {
        console.log('State should be updated now. Metrics data length in state should be:', cleanMetricsData.length);
      }, 100);
      
    } catch (error) {
      console.error('Error fetching past results:', error);
      // Same rule for failures: a superseded denial must not overwrite the
      // state of the request that replaced it.
      if (requestId !== requestIdRef.current) return;
      if (isPermissionDenied(error)) {
        // The Message variant, which inlines the Permissions-app URL: there is
        // no button here to replace that link with.
        showError(getPermissionDeniedMessage(error), 'fetch');
      } else {
        showError('Failed to fetch past results. Please try again.', 'fetch');
      }
      setCashflowData([]);
      setMetricsData([]);
    } finally {
      // Only the newest request owns the spinner; clearing it from a superseded
      // one would hide that a newer fetch is still running.
      if (requestId === requestIdRef.current) {
        setTableLoading(false);
      }
    }
  };

  // Wait for the submitted run by its own Batch tasks' telemetry, then load its
  // results once. /cashflow and /metrics also return earlier runs of the same
  // config, so "rows came back" says nothing about whether this run finished.
  // A newer fetchPastResults or invalidateActiveFetch (the user edited the
  // inputs) supersedes the wait: its results would describe inputs the user has
  // since changed.
  useEffect(() => {
    if (!activeRun) return undefined;
    const generation = requestIdRef.current;
    const runIds = activeRun.tasks.map((task) => `${activeRun.batchJobId}_${task}`);
    // Terminal statuses seen so far; a terminal status never changes. The poll asks
    // for every row of this run, but an older server answers with its default
    // window (active rows plus the latest 50 outcomes), where an early outcome can
    // age out before the last task finishes, so seen outcomes are remembered.
    const finished = new Map();
    // A task with no row and nothing of the run active: not recorded yet, or (on an
    // older server) aged out. IDLE_POLLS of that ends the wait either way.
    let idlePolls = 0;
    let failedPolls = 0;
    let cancelled = false;
    let inFlight = false;
    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    const current = () => !cancelled && generation === requestIdRef.current;
    const endWait = () => {
      setTableLoading(false);
      setIsWaitingForConfig(false);
    };

    const poll = async () => {
      if (inFlight) return;
      if (!current()) {
        stopPolling();
        if (!cancelled) setIsWaitingForConfig(false);
        return;
      }
      inFlight = true;
      try {
        const response = await fetchScenarioTelemetry(activeRun.configId, undefined, activeRun.batchJobId);
        if (!current()) return;
        failedPolls = 0;
        const executions = Array.isArray(response?.data?.executions) ? response.data.executions : [];
        let active = false;
        executions.forEach((row) => {
          if (!runIds.includes(row.RUN_ID)) return;
          if (TERMINAL.has(row.STATUS)) finished.set(row.RUN_ID, row.STATUS);
          else active = true;
        });
        const unseen = runIds.length - finished.size;
        if (unseen > 0) {
          // Still running: keep waiting, however long the tasks take.
          if (active) {
            idlePolls = 0;
            return;
          }
          idlePolls += 1;
          if (idlePolls < IDLE_POLLS) return;
        }

        stopPolling();
        if (finished.size === 0) {
          // Nothing of the run ever reported: there is no outcome to show results for.
          setCashflowData([]);
          setMetricsData([]);
          showError(RUN_STATUS_MISSING, 'run');
          endWait();
          return;
        }
        const statuses = [...finished.values()];
        // Results load only when a task of this run is confirmed to have produced
        // them: /cashflow and /metrics also hold earlier runs, so without one every
        // row shown could be an earlier run's.
        const produced = statuses.some((status) => status === 'COMPLETED' || status === 'COMPLETED WITH ERRORS');
        if (!produced) {
          setCashflowData([]);
          setMetricsData([]);
          let message = RUN_FAILED;
          if (unseen > 0) message = RUN_NONE_CONFIRMED;
          else if (statuses.every((status) => status === 'SKIPPED')) message = RUN_ALL_SKIPPED;
          showError(message, 'run');
          endWait();
          return;
        }
        // Settled separately: one table failing to load must not discard the other.
        const [cashflowResult, metricsResult] = await Promise.allSettled([
          fetchCashflow(activeRun.payload),
          fetchMetrics(activeRun.payload),
        ]);
        if (!current()) return;
        if (cashflowResult.status === 'fulfilled') {
          setCashflowData(cleanDataForGrid(cashflowResult.value?.data || []));
        }
        if (metricsResult.status === 'fulfilled') {
          setMetricsData(cleanDataForGrid(metricsResult.value?.data || []));
        }
        const failure = [cashflowResult, metricsResult].find((result) => result.status === 'rejected');
        if (failure) {
          console.error('Error loading the scenario run results:', failure.reason);
          if (!notifyUngatedFailure(showNotification, failure.reason)) showError(RUN_STATUS_UNAVAILABLE, 'run');
        } else if (unseen > 0) {
          showError(RUN_UNCONFIRMED, 'run');
        } else if (statuses.some((status) => status !== 'COMPLETED')) {
          showError(RUN_PARTIAL, 'run');
        }
        endWait();
      } catch (error) {
        if (!current()) return;
        console.error('Error waiting for the scenario run:', error);
        // An access problem will not fix itself; anything else may be transient.
        const handled = notifyUngatedFailure(showNotification, error);
        failedPolls += 1;
        if (!handled && failedPolls < FAILED_POLLS) return;
        stopPolling();
        if (!handled) showError(RUN_STATUS_UNAVAILABLE, 'run');
        endWait();
      } finally {
        inFlight = false;
      }
    };

    setIsWaitingForConfig(true);
    setTableLoading(true);
    poll();
    pollingIntervalRef.current = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [activeRun, showNotification]);

  const handleFormSubmit = async (configData) => {
    // A run of a loaded config saves it first, and the server returns the MODIFIED_DATE
    // that save stamped even when it then refuses the run. The next save or run of
    // the same config must send it.
    const adoptStampedToken = (modifiedDate) => {
      if (!configData?.ID || !modifiedDate) return;
      setFormData(prev => (prev.id === configData.ID ? { ...prev, modifiedDate } : prev));
    };
    // This run owns the error state now. An automatic fetchPastResults already
    // in flight would otherwise land later and clear this run's failure with
    // its own showError(null).
    invalidateActiveFetch();
    // Editing the inputs while /run_scenario is pending starts a newer fetch; this
    // run's answer must then not start a wait whose results describe old inputs.
    const submitGeneration = requestIdRef.current;
    setLoading(true);
    showError(null);

    // Clear existing data and show loading wheels immediately
    setCashflowData([]);
    setMetricsData([]);
    setTableLoading(true);
    
    try {
      if (metricsMode) {
        // Metrics Mode: Call /api/metrics instead of runScenario
        const payload = {
          cusips: formData.cusips,
          asofdt: formData.asofdt,
          scenarios: formData.scenarios,
          env: formData.env
        };
        
        console.log('Metrics Mode - calling /api/metrics with payload:', payload);
        const response = await fetchMetricsMode(payload);
        
        console.log('Metrics Mode API Response:', response?.data);
        
        // The API returns { cashflows, analytics }
        const cleanCashflowData = cleanDataForGrid(response?.data?.cashflows || []);
        const cleanMetricsData = cleanDataForGrid(response?.data?.analytics || []);

        console.log('After cleaning (metrics mode):');
        console.log('- Clean cashflow length:', cleanCashflowData.length);
        console.log('- Clean analytics length:', cleanMetricsData.length);

        setCashflowData(cleanCashflowData);
        setMetricsData(cleanMetricsData);
        setTableLoading(false);
      } else {
        // Standard mode: Run scenario and poll for results
        setIsWaitingForConfig(true);
        const response = await runScenario(configData);
        const { configId, batchJobId, tasks } = response.data;
        adoptStampedToken(response.data.modified_date);
        if (requestIdRef.current !== submitGeneration) {
          // The newer fetch owns the grids and their spinner.
          setIsWaitingForConfig(false);
        } else if (!batchJobId || !Array.isArray(tasks) || tasks.length === 0) {
          // Nothing to wait on: never fall back to "any rows" for this run.
          showError(RUN_UNTRACKABLE, 'run');
          setTableLoading(false);
          setIsWaitingForConfig(false);
        } else {
          setActiveRun({
            configId,
            batchJobId,
            tasks,
            payload: {
              cusips: formData.cusips,
              configId,
              scenarios: formData.scenarios,
              asofdt: formData.asofdt,
              env: formData.env,
            },
          });
        }
      }
    } catch (error) {
      console.error('Error in Portfolio analysis:', error);
      if (!metricsMode) adoptStampedToken(error.response?.data?.modified_date);
      // Only the standard branch calls a gated endpoint (run_scenario). The
      // metrics branch posts to /portfolio_analytics, which the server does not
      // implement — so a permission_denied cannot come from it, and offering to
      // request a role there would be meaningless.
      const notify = metricsMode ? notifyUngatedFailure : notifyPermissionError;
      if (!notify(showNotification, error)) {
        showError(serverErrorText(error, {
          fallback: 'Failed to run analysis. Please try again.',
          unknown: RUN_OUTCOME_UNKNOWN,
        }), 'run');
      }
      // Reset loading states on error
      setTableLoading(false);
      setIsWaitingForConfig(false);
    } finally {
      setLoading(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // Monitor metrics data changes with detailed logging
  useEffect(() => {
    console.log('=== METRICS DATA CHANGED IN MAIN ===');
    console.log('New metricsData:', metricsData);
    console.log('Type:', typeof metricsData);
    console.log('Is Array:', Array.isArray(metricsData));
    console.log('Length:', metricsData?.length);
    
    if (metricsData && metricsData.length > 0) {
      console.log('First record keys:', Object.keys(metricsData[0]));
      console.log('First record values sample:', {
        CUSIP: metricsData[0].CUSIP,
        CLEAN_PX: metricsData[0].CLEAN_PX,
        BOOK_VALUE: metricsData[0].BOOK_VALUE
      });
    }
  }, [metricsData]);

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3} direction="column">
        {/* Portfolio Analysis Toolbar */}
        <Grid item xs={12}>
          <PortfolioForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleFormSubmit}
            loading={loading}
            error={error}
            isFormComplete={isFormComplete}
            isWaitingForConfig={isWaitingForConfig}
            userId={userId}
            metricsMode={metricsMode}
            onMetricsModeChange={setMetricsMode}
            selectedLotIds={selectedLotIds}
            onLotIdsChange={setSelectedLotIds}
            availableLotIds={availableLotIds}
            hasData={cashflowData.length > 0 && metricsData.length > 0}
          />
        </Grid>

        {/* Cashflow Table */}
        <Grid item xs={12}>
          <CusipTables
            cashflowData={filteredCashflowData}
            metricsData={null} // Only show cashflow table
            tableLoading={tableLoading}
            formData={formData}
            showDebugInfo={true}
            tableType="cashflow"
          />
        </Grid>

        {/* Metrics Table */}
        <Grid item xs={12}>
          <CusipTables
            cashflowData={null} // Only show metrics table
            metricsData={filteredMetricsData}
            tableLoading={tableLoading}
            formData={formData}
            showDebugInfo={true}
            tableType="metrics"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PortfolioAnalysis;
